<template>
    <div>
        <!-- Loud impersonation banner (fixed top) when a tenant override is active -->
        <div
            v-if="tenantOverride"
            :style="bannerStyle"
            @click="openPanel"
        >
            <span>⚠ IMPERSONATING TENANT: <strong>{{ tenantOverride }}</strong> — local data</span>
            <button
                class="btn btn-sm btn-light py-0 px-2"
                style="font-size: 0.72rem;"
                @click.stop="exitTenant"
            >Exit</button>
        </div>

        <!-- Collapsed pill button -->
        <div
            v-if="!panelOpen"
            class="dev-pill"
            :style="pillStyle"
            @click="openPanel"
        >
            <span class="fw-bold">DEV</span>
            <span v-if="overrideCount > 0" class="badge bg-light text-dark ms-1">{{ overrideCount }}</span>
        </div>

        <!-- Expanded panel -->
        <div
            v-if="panelOpen"
            class="dev-panel card shadow-lg"
            :style="panelStyle"
        >
            <!-- Header -->
            <div class="card-header bg-danger text-white d-flex align-items-center justify-content-between py-2 px-3">
                <span class="fw-bold">Dev Panel</span>
                <div class="d-flex align-items-center gap-2">
                    <div class="form-check form-switch mb-0">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="isActive"
                            @change="toggleActive"
                        />
                    </div>
                    <span
                        role="button"
                        style="cursor: pointer; font-size: 1.1rem; line-height: 1; opacity: 0.85;"
                        @click="closePanel"
                    >&times;</span>
                </div>
            </div>

            <div class="card-body p-0" style="max-height: 60vh; overflow-y: auto;">
                <!-- Tools (playground routes only exist in dev builds) -->
                <div v-if="playgroundAvailable" class="px-3 py-2 border-bottom">
                    <div class="text-muted small fw-bold mb-2 text-start">TOOLS</div>
                    <router-link
                        :to="{ name: 'Playground' }"
                        class="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                        @click="closePanel"
                    >
                        <i class="fas fa-umbrella-beach"></i>
                        <span>Open Playground</span>
                    </router-link>
                </div>

                <!-- Current State -->
                <div class="px-3 py-2 border-bottom">
                    <div class="text-muted small fw-bold mb-1 text-start">STATE</div>
                    <div class="d-flex justify-content-between small">
                        <span>Tenant</span>
                        <span class="fw-bold">{{ customerID }}</span>
                    </div>
                    <div class="d-flex justify-content-between small">
                        <span>Real Role</span>
                        <span class="fw-bold">{{ realRole || 'none' }}</span>
                    </div>
                    <div class="d-flex justify-content-between small">
                        <span>Active Role</span>
                        <span
                            class="fw-bold"
                            :class="{ 'text-warning': roleOverride }"
                        >{{ activeRole || 'none' }}</span>
                    </div>
                </div>

                <!-- Tenant impersonation (localhost/staging only) -->
                <div v-if="canSwitchTenant" class="px-3 py-2 border-bottom">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="text-muted small fw-bold">TENANT</div>
                        <button
                            v-if="tenantOverride"
                            class="btn btn-outline-secondary btn-sm py-0 px-1"
                            style="font-size: 0.7rem;"
                            @click="exitTenant"
                        >Exit</button>
                    </div>
                    <input
                        v-model="tenantInput"
                        list="dev-known-tenants"
                        type="text"
                        class="form-control form-control-sm mb-2"
                        placeholder="tenant subdomain (e.g. armstrong)"
                        @keyup.enter="switchTenant(tenantInput)"
                    />
                    <datalist id="dev-known-tenants">
                        <option v-for="t in knownTenants" :key="t" :value="t" />
                    </datalist>
                    <button
                        class="btn btn-warning btn-sm w-100"
                        :disabled="!tenantInput.trim()"
                        @click="switchTenant(tenantInput)"
                    >Switch &amp; re-login</button>
                    <div class="text-muted mt-1" style="font-size: 0.7rem;">
                        Reloads and logs in as that tenant (admin override). Data must be
                        loaded locally via <code>load-tenant.sh</code>.
                    </div>
                </div>

                <!-- Role Impersonation -->
                <div class="px-3 py-2 border-bottom">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="text-muted small fw-bold">ROLE</div>
                        <button
                            v-if="roleOverride"
                            class="btn btn-outline-secondary btn-sm py-0 px-1"
                            style="font-size: 0.7rem;"
                            @click="resetRole"
                        >Reset</button>
                    </div>
                    <div class="d-flex flex-wrap gap-1">
                        <button
                            v-for="role in roles"
                            :key="role"
                            class="btn btn-sm"
                            :class="roleOverride === role ? 'btn-warning' : 'btn-outline-secondary'"
                            @click="setRole(role)"
                        >{{ role }}</button>
                    </div>
                </div>

                <!-- Feature Flags -->
                <div v-if="canPreviewPlans" class="px-3 py-2 border-bottom">
                    <label for="dev-plan-preview" class="text-muted small fw-bold mb-2">PLAN PREVIEW</label>
                    <select
                        id="dev-plan-preview" class="form-select form-select-sm"
                        :value="store.state.devOverrides.planPreviewID || ''"
                        @change="setPlanPreview($event.target.value)">
                        <option value="">Tenant configuration</option>
                        <option v-for="plan in customerPlans" :key="plan.id" :value="plan.id">{{ plan.name }}</option>
                        <option v-if="store.state.devOverrides.planPreviewID === 'custom'" value="custom" disabled>Custom preview</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="setPlanPreview('')">Restore tenant configuration</button>
                    <p class="small text-muted mt-2 mb-0">Preview changes visibility in this browser. Actions still use the configured backend. Customer features and billing remain unchanged.</p>
                </div>
                <div class="px-3 py-2">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="text-muted small fw-bold">FLAGS</div>
                        <button
                            v-if="hasAnyFlagOverride"
                            class="btn btn-outline-secondary btn-sm py-0 px-1"
                            style="font-size: 0.7rem;"
                            @click="resetAllFlags"
                        >Reset all</button>
                    </div>
                    <input
                        v-model="flagSearch"
                        type="text"
                        class="form-control form-control-sm mb-2"
                        placeholder="Search flags..."
                    />
                    <div style="max-height: 250px; overflow-y: auto;">
                        <div
                            v-for="flag in filteredFlags"
                            :key="flag.key"
                            class="d-flex align-items-center justify-content-between py-1"
                            style="font-size: 0.78rem;"
                        >
                            <div class="d-flex align-items-center gap-1 text-truncate" style="max-width: 200px;">
                                <span
                                    class="rounded-circle d-inline-block flex-shrink-0"
                                    :style="{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: flag.realValue ? '#198754' : '#adb5bd'
                                    }"
                                ></span>
                                <span class="text-truncate" :title="flag.key">{{ flag.key }}</span>
                            </div>
                            <button
                                class="btn btn-sm py-0 px-1 flex-shrink-0"
                                :class="flagButtonClass(flag)"
                                style="font-size: 0.7rem; min-width: 40px;"
                                @click="cycleFlagOverride(flag.key)"
                            >{{ flagButtonLabel(flag) }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Flags, getRealFlagValue, hasFeatureFlag } from "@/classes/FeatureFlags";
import { canAccessReportRoute } from "@/views/reports/reportConfig";
import { customerPlans, usesPlanFeatureScope } from "@/classes/customerPlans";

const store = useStore();
const router = useRouter();

// Playground routes are registered only in dev builds (router.js gates them on
// import.meta.env.DEV) — rendering a router-link to a missing named route throws.
const playgroundAvailable = router.hasRoute("Playground");
const flagSearch = ref("");
const roles = ["admin", "user", "auditor", "supplier"];

// Tenants with prod data routinely loaded locally (datalist suggestions only —
// you can type any subdomain you've loaded via load-tenant.sh).
const knownTenants = [
    "armstrong", "christies", "rudysfarm", "skea",
    "sourdoughdave", "thehappacafe", "topmeal", "wildflour", "windmill"
];
const tenantInput = ref("");

const isActive = computed(() => store.state.devOverrides.active);
const panelOpen = computed(() => store.state.devOverrides.panelOpen);
const roleOverride = computed(() => store.state.devOverrides.roleOverride);
const flagOverrides = computed(() => store.state.devOverrides.flagOverrides);
const tenantOverride = computed(() => store.state.devOverrides.tenantOverride);
const customerID = computed(() => store.state.customerID);
const isMobile = computed(() => store.state.isMobile);

// Tenant impersonation is dev-only: enabled in the Vite dev build, on localhost,
// or when the active tenant is "staging". Never offered against a real prod tenant.
const canSwitchTenant = computed(() => {
    if (import.meta.env.MODE === "development") return true;
    const host = window.location.hostname;
    return (
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "192.168.1.4" ||
        store.state.customerID === "staging"
    );
});

const realRole = computed(() => store.getters.realUserRole());
const canPreviewPlans = computed(() => import.meta.env.DEV || ["localhost", "127.0.0.1"].includes(window.location.hostname) || customerID.value === "staging");
function setPlanPreview(planID) {
    if (!canPreviewPlans.value) return;
    store.commit("setDevPlanPreview", planID || null);
    const route = router.currentRoute.value;
    const reportAllowed = canAccessReportRoute(route.name, {
        has: hasFeatureFlag,
        role: (role) => role === store.getters.userRole(),
        planScoped: usesPlanFeatureScope(store.state.client, store.state.devOverrides)
    });
    if (!reportAllowed || (route.meta.featureFlag && !hasFeatureFlag(route.meta.featureFlag))) {
        void router.push("/");
    }
}
const activeRole = computed(() => store.getters.userRole());

const overrideCount = computed(() => {
    let count = 0;
    if (roleOverride.value) count++;
    count += Object.keys(flagOverrides.value).length;
    return count;
});

const hasAnyFlagOverride = computed(() => Object.keys(flagOverrides.value).length > 0);

const allFlags = computed(() => {
    return Object.entries(Flags).map(([name, key]) => ({
        name,
        key,
        realValue: getRealFlagValue(key),
        override: key in flagOverrides.value ? flagOverrides.value[key] : null
    }));
});

const filteredFlags = computed(() => {
    const search = flagSearch.value.toLowerCase();
    if (!search) return allFlags.value;
    return allFlags.value.filter(f => f.key.toLowerCase().includes(search));
});

// When the impersonation strip is showing along the bottom edge, lift the
// pill and panel above it so neither is covered.
const bannerLift = computed(() => (tenantOverride.value ? "2rem" : "0px"));
const pillBottom = computed(() => {
    if (!isMobile.value) return "20px";
    // The chat composer occupies the old mobile pill position. Keep the
    // collapsed dev control in the reserved navigation space on that page.
    return router.currentRoute.value.name === "Chat"
        ? "var(--forkto-mobile-nav-optical-bottom)"
        : "5rem";
});

const pillStyle = computed(() => ({
    position: "fixed",
    bottom: `calc(${pillBottom.value} + ${bannerLift.value})`,
    right: "16px",
    zIndex: 1999,
    backgroundColor: "#dc3545",
    color: "white",
    padding: "6px 14px",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "0.85rem",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
}));

const panelStyle = computed(() => ({
    position: "fixed",
    bottom: `calc(${isMobile.value ? "5rem" : "20px"} + ${bannerLift.value})`,
    right: "16px",
    zIndex: 1999,
    width: "340px",
    maxHeight: "80vh",
    fontSize: "0.85rem"
}));

// Bottom edge, not top: a fixed strip at the top sat over the app top bar
// and hid the logo, context pill, and search.
const bannerStyle = {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    zIndex: 2000,
    backgroundColor: "#dc3545",
    color: "white",
    padding: "4px 12px",
    fontSize: "0.8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    cursor: "pointer",
    boxShadow: "0 -2px 6px rgba(0,0,0,0.3)"
};

function openPanel() {
    store.commit("setDevOverridesActive", true);
    store.commit("setDevPanelOpen", true);
}

function closePanel() {
    store.commit("setDevPanelOpen", false);
}

function switchTenant(sub) {
    const tenant = (sub || "").trim().toLowerCase();
    if (!tenant) return;
    // Keep the panel reachable after the reload so you can Exit again.
    store.commit("setDevOverridesActive", true);
    store.commit("setDevTenantOverride", tenant);
    // Reload so the boot sequence re-reads the override as the active Customer-ID
    // and lands on the login screen for that tenant (log in via admin override).
    window.location.reload();
}

function exitTenant() {
    store.commit("setDevTenantOverride", null);
    window.location.reload();
}

function toggleActive(e) {
    const active = e.target.checked;
    store.commit("setDevOverridesActive", active);
    if (!active) {
        store.commit("setDevRoleOverride", null);
        // Clear all flag overrides
        for (const key of Object.keys(flagOverrides.value)) {
            store.commit("setDevFlagOverride", { flag: key, value: undefined });
        }
        // Re-save with empty overrides
        store.commit("clearDevOverrides");
        store.commit("setDevPanelOpen", true);
    }
}

function setRole(role) {
    if (roleOverride.value === role) {
        store.commit("setDevRoleOverride", null);
    } else {
        store.commit("setDevRoleOverride", role);
    }
}

function resetRole() {
    store.commit("setDevRoleOverride", null);
}

function cycleFlagOverride(flagKey) {
    const current = flagKey in flagOverrides.value ? flagOverrides.value[flagKey] : null;
    if (current === null) {
        // null -> true (force on)
        store.commit("setDevFlagOverride", { flag: flagKey, value: true });
    } else if (current === true) {
        // true -> false (force off)
        store.commit("setDevFlagOverride", { flag: flagKey, value: false });
    } else {
        // false -> null (use real) - remove the override
        store.commit("setDevFlagOverride", { flag: flagKey, value: undefined });
    }
}

function flagButtonClass(flag) {
    if (flag.override === null) return "btn-outline-secondary";
    if (flag.override === true) return "btn-success";
    if (flag.override === false) return "forkto-state-action--failure";
    return "btn-outline-secondary";
}

function flagButtonLabel(flag) {
    if (flag.override === null) return "real";
    if (flag.override === true) return "ON";
    if (flag.override === false) return "OFF";
    return "real";
}

function resetAllFlags() {
    for (const key of Object.keys(flagOverrides.value)) {
        store.commit("setDevFlagOverride", { flag: key, value: undefined });
    }
}
</script>
